import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Zap } from "lucide-react";
import { goTo } from "./Shared";
import { useLang } from "../../i18n";

const LangToggle = ({ className = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center rounded-full border border-[#9B7AE8]/25 bg-white/70 p-1 ${className}`} data-testid="lang-toggle">
      {["tr", "en", "de"].map((l) => (
        <button
          key={l}
          data-testid={`lang-toggle-${l}`}
          onClick={() => setLang(l)}
          className={`rounded-full px-3 py-1 font-nav text-[10px] font-bold uppercase tracking-[0.15em] transition-all duration-300 ${
            lang === l ? "bg-[#C9B0FF] text-[#221A3D]" : "text-[#8E85AC] hover:text-[#9B7AE8]"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { t } = useLang();
  const LINKS = [
    { label: t.nav.nedir, href: "#nedir" },
    { label: t.nav.receteler, href: "#receteler" },
    { label: t.nav.donanim, href: "#donanim" },
    { label: t.nav.neden, href: "#neden" },
    { label: t.nav.isbirligi, href: "#nasil" },
  ];
  const nav = (href) => {
    setOpen(false);
    goTo(href);
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-[#9B7AE8]/15 bg-white/75 backdrop-blur-xl">
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <button
          data-testid="nav-logo-button"
          onClick={() => window.__lenis?.scrollTo(0)}
          className="flex items-center gap-3"
        >
          <img src="/assets/logo.png" alt="Shake & Shake" className="h-9 w-auto" />
        </button>

        <nav className="hidden items-center gap-5 lg:flex" data-testid="nav-links">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              onClick={() => nav(l.href)}
              className="group relative font-nav text-[10px] font-semibold uppercase tracking-[0.16em] text-[#6B6386] transition-colors duration-300 hover:text-[#9B7AE8]"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-0.5 w-0 rounded-full bg-[#C9B0FF] transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LangToggle />
          <button
            data-testid="nav-cta-button"
            onClick={() => nav("#talep")}
            className="hidden items-center gap-2 rounded-full bg-[#C9B0FF] px-5 py-2.5 font-nav text-[11px] font-semibold uppercase tracking-[0.18em] text-[#221A3D] shadow-[0_10px_30px_rgba(155,122,232,0.35)] transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_40px_rgba(155,122,232,0.5)] sm:flex"
          >
            <Zap size={13} strokeWidth={2.5} />
            {t.nav.iletisim}
          </button>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="text-[#9B7AE8] lg:hidden"
            aria-label={t.nav.menu}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-[#9B7AE8]/15 bg-white/95 lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-5">
              {[...LINKS, { label: t.nav.iletisim, href: "#talep" }].map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-link-${l.href.slice(1)}`}
                  onClick={() => nav(l.href)}
                  className="py-3 text-left font-nav text-xs font-semibold uppercase tracking-[0.22em] text-[#6B6386] transition-colors hover:text-[#9B7AE8]"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
